import type { SiteContext } from '@smarton-pbx/shared';
import { t } from '../i18n';

/** Black or white, whichever reads better on the given background. */
export function readableTextOn(hex: string): string {
  const [r, g, b] = [1, 3, 5].map((i) => {
    const channel = Number.parseInt(hex.slice(i, i + 2), 16) / 255;
    return channel <= 0.03928 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4;
  }) as [number, number, number];
  const luminance = 0.2126 * r + 0.7152 * g + 0.0722 * b;
  return luminance > 0.4 ? '#111111' : '#ffffff';
}

export function logoUrl(version: string): string {
  return `/api/branding/logo?v=${version}`;
}

/** Applies the tenant's name, colours and favicon to the page. Never our own name. */
export function applyBranding(context: SiteContext): void {
  const root = document.documentElement.style;
  if (context.kind === 'platform') {
    document.title = t('Platform console');
    if (context.faviconVersion) setLink('icon', `/api/branding/favicon?v=${context.faviconVersion}`);
    return;
  }
  const { branding } = context;
  document.title = branding.productName;
  root.setProperty('--brand', branding.primaryColor);
  root.setProperty('--brand-text', readableTextOn(branding.primaryColor));
  root.setProperty('--accent', branding.accentColor);

  let icon = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
  if (branding.faviconVersion) {
    if (!icon) {
      icon = document.createElement('link');
      icon.rel = 'icon';
      document.head.append(icon);
    }
    icon.href = `/api/branding/favicon?v=${branding.faviconVersion}`;
  } else {
    icon?.remove();
  }

  // Installable as the tenant's own app (name, colours and icons from the API).
  const version = [branding.logoVersion, branding.faviconVersion, branding.primaryColor.slice(1)].join('-');
  setLink('manifest', `/api/branding/manifest.webmanifest?v=${version}`);
  setLink('apple-touch-icon', `/api/branding/app-icon-192.png?v=${version}`);
  let theme = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
  if (!theme) {
    theme = document.createElement('meta');
    theme.name = 'theme-color';
    document.head.append(theme);
  }
  theme.content = branding.primaryColor;
}

function setLink(rel: string, href: string): void {
  let link = document.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!link) {
    link = document.createElement('link');
    link.rel = rel;
    document.head.append(link);
  }
  link.href = href;
}
